import React, { createContext, useContext, useEffect, useMemo, useReducer } from 'react';
import { getConfig, questionContext, setConfig, types } from '@oneclick/react-lemonade-editor';
import { getStorage, setStorage } from '../utils/storegeUtils';

const PERSIST_IMMEDIATE_FEEDBACK = 'config_immediateFeedback';
const PERSIST_TOOLBAR_FEEDBACK = 'config_toolbarFeedback';
const PERSIST_MATH_ENGINE = 'config_mathEngine';
const PERSIST_LANG = 'i18nextLng';
const PERSIST_BLUEBERRY_STYLE = 'config_blueberryStyle';
const PERSIST_SHOW_AUTO_FEEDBACK = 'config_showAutoFeedback';
const LemonadeConfigContext = createContext();

const actions = {
  CHANGE_LANG: 'CHANGE_LANG',
  CHANGE_IMMEDIATE_FEEDBACK: 'CHANGE_IMMEDIATE_FEEDBACK',
  CHANGE_TOOLBAR_FEEDBACK: 'CHANGE_TOOLBAR_FEEDBACK',
  CHANGE_MATH_ENGINE: 'CHANGE_MATH_ENGINE',
  ENABLE_QUESTION_BULLET: 'ENABLE_QUESTION_BULLET',
  CHANGE_CONTEXT: 'CHANGE_CONTEXT',
  APPLY_BLUEBERRY_STYLE: 'APPLY_BLUEBERRY_STYLE',
  CHANGE_SHOW_AUTO_FEEBACK: 'CHANGE_SHOW_AUTO_FEEBACK',
};

const initialState = {
  lang: getStorage(PERSIST_LANG) || 'en',
  immediateFeedback: getStorage(PERSIST_IMMEDIATE_FEEDBACK) === 'true' || false,
  toolbarFeedback: getStorage(PERSIST_TOOLBAR_FEEDBACK) === 'true' || false,
  mathEngine: getStorage(PERSIST_MATH_ENGINE) || 'mathjax',
  enableQuestionBullet: false,
  context: questionContext.NONE,
  blueberryStyle: getStorage(PERSIST_BLUEBERRY_STYLE) === 'true' || false,
  showAutoFeedback: getStorage(PERSIST_SHOW_AUTO_FEEDBACK) === 'true' || false,
};

function lemonadeConfigReducer(state, action) {
  switch (action.type) {
    case actions.CHANGE_LANG: {
      setStorage(PERSIST_LANG, action.payload.lang);
      setConfig({ lang: action.payload.lang });
      return { ...state, lang: action.payload.lang };
    }
    case actions.CHANGE_IMMEDIATE_FEEDBACK: {
      setStorage(PERSIST_IMMEDIATE_FEEDBACK, action.payload.immediateFeedback);
      return { ...state, immediateFeedback: action.payload.immediateFeedback };
    }
    case actions.CHANGE_TOOLBAR_FEEDBACK: {
      setStorage(PERSIST_TOOLBAR_FEEDBACK, action.payload.toolbarFeedback);
      setConfig({ individualFeedbackTooltip: action.payload.toolbarFeedback });
      return { ...state, toolbarFeedback: action.payload.toolbarFeedback };
    }
    case actions.CHANGE_MATH_ENGINE: {
      setStorage(PERSIST_MATH_ENGINE, action.payload.mathEngine);
      setConfig({ mathRenderEngine: action.payload.mathEngine });
      return { ...state, mathEngine: action.payload.mathEngine };
    }
    case actions.ENABLE_QUESTION_BULLET: {
      return { ...state, enableQuestionBullet: action.payload };
    }
    case actions.CHANGE_CONTEXT: {
      return { ...state, context: action.payload };
    }
    case actions.APPLY_BLUEBERRY_STYLE: {
      setStorage(PERSIST_BLUEBERRY_STYLE, action.payload.blueberryStyle);
      return { ...state, blueberryStyle: action.payload.blueberryStyle };
    }
    case actions.CHANGE_SHOW_AUTO_FEEBACK: {
      setStorage(PERSIST_SHOW_AUTO_FEEDBACK, action.payload.showAutoFeedback);
      setConfig({ showAutoFeedback: action.payload.showAutoFeedback });
      return { ...state, showAutoFeedback: action.payload.showAutoFeedback };
    }
    default: {
      throw new Error(`Unsupported action type: ${action.type}`);
    }
  }
}

/**
 *
 * Pasarle como props forceShowAutoFeedback !== null para forzar el valor de showAutoFeedback
 * @returns
 */
function useLemonadeConfig(props = {}) {
  const forceShowAutoFeedback = Object.prototype.hasOwnProperty.call(props, 'forceShowAutoFeedback')
    ? props.forceShowAutoFeedback
    : null;
  const context = useContext(LemonadeConfigContext);
  if (!context) {
    throw new Error(`useLemonadeConfig must be used within a LemonadeConfigProvider`);
  }
  const [state, dispatch] = context;

  useEffect(() => {
    setConfig({
      individualFeedbackTooltip: state.toolbarFeedback,
      // lang: state.lang,
      lang: 'en',
      mathRenderEngine: state.mathEngine,
      chartsEnable: true,
      showSampleAnswer: true,
      autoFocus: 'enabled',
      desmosApiKey: 'dcb31709b452b1cf9dc26972add0fda6',
      googlePicker: {
        clientId: '681820977496-b7n69qsgbu381hlffngdkmt4d69e0cm3.apps.googleusercontent.com',
        developerKey: 'AIzaSyAzbKajVaLQsBX7YxQIEad6APcLltIJ7Xg',
      },
      configExercise: {
        [types.other.PathWay]: {
          // posición en que aparecen las instrucciones respecto del tablero
          //  values = (TOP | LEFT | BOTTOM | RIGHT)
          instructions_position: 'TOP',
        },
        [types.classify.MatchList]: {
          isElementFeedbackOnlyNotEmptyValue: false, // == Si es false se va a mostrar tambien cuando este vacia la respuesta
        },
        [types.classify.LinkingLines]: {
          isElementFeedbackOnlyNotEmptyValue: false, // == Si es false se va a mostrar tambien cuando este vacia la respuesta
        },
      },
      hostName: 'lemontree',
      thousandsSeparator: ',',
      attachmentsEnable: true,
      showAIFeedbackGenerator: true,
      showAutoFeedback: forceShowAutoFeedback !== null ? forceShowAutoFeedback : state.showAutoFeedback,
      configEditor: {
        answerOptions: {
          imageDefaultSize: {
            height: null,
            width: 200,
          },
        },
        questions: {
          imageDefaultSize: {
            height: null,
            width: 200,
          },
        },
      },
    });
    window.lemonadeConfig = getConfig();
    window.enableQuestionBullet = (enable = true) => {
      dispatch({ type: actions.ENABLE_QUESTION_BULLET, payload: enable });
    };
    window.changeLemonadeContext = (context = questionContext.NONE) => {
      dispatch({ type: actions.CHANGE_CONTEXT, payload: context });
    };
  }, []);

  useEffect(() => {
    if (forceShowAutoFeedback !== null) {
      dispatch({ type: actions.CHANGE_SHOW_AUTO_FEEBACK, payload: { showAutoFeedback: forceShowAutoFeedback } });
    }
  }, [forceShowAutoFeedback]);

  const changeLang = (lang) => dispatch({ type: actions.CHANGE_LANG, payload: { lang } });
  const setImmediateFeedback = (enable) =>
    dispatch({ type: actions.CHANGE_IMMEDIATE_FEEDBACK, payload: { immediateFeedback: enable } });
  const setToolbarFeedback = (enable) =>
    dispatch({ type: actions.CHANGE_TOOLBAR_FEEDBACK, payload: { toolbarFeedback: enable } });
  const setMathEngine = (engine) => dispatch({ type: actions.CHANGE_MATH_ENGINE, payload: { mathEngine: engine } });
  const applyBlueberryStyle = (enable) =>
    dispatch({ type: actions.APPLY_BLUEBERRY_STYLE, payload: { blueberryStyle: enable } });
  const setShowAutoFeedback = (enable) =>
    dispatch({ type: actions.CHANGE_SHOW_AUTO_FEEBACK, payload: { showAutoFeedback: enable } });

  return {
    lemonadeConfig: state,
    dispatch,
    changeLang,
    setImmediateFeedback,
    setToolbarFeedback,
    setMathEngine,
    applyBlueberryStyle,
    setShowAutoFeedback,
  };
}

function LemonadeConfigProvider(props) {
  const [state, dispatch] = useReducer(lemonadeConfigReducer, initialState);
  const value = useMemo(() => [state, dispatch], [state]);
  return <LemonadeConfigContext.Provider value={value} {...props} />;
}

export { LemonadeConfigProvider, useLemonadeConfig };
